/**
 * Step 4: Resolve Relations
 *
 * Interactive resolution screen for ambiguous relational values.
 * Groups are displayed by field → raw value, showing candidates with
 * confidence scores. Nothing ambiguous is ever silently applied.
 *
 * When no match exists, users can create a new record inline using
 * the target entity's registered UploadSchema.
 */

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { useTranslation } from 'react-i18next';
import {
	LookupData,
	ResolutionGroup,
	ScoredCandidate,
	UploadFieldDef,
	UploadSchema,
} from '../utils/types';
import { searchLookup } from '../utils/resolve';
import { canCreateInline } from '../utils/schemas';

interface StepResolveRelationsProps {
	groups: ResolutionGroup[];
	onResolve: (key: string, selectedId: number | null, selectedLabel: string | null) => void;
	onSkip: (key: string) => void;
	onAutoResolve: () => void;
	onCreateRecord: (
		groupKey: string,
		contextKey: string,
		apiEndpoint: string,
		payload: Record<string, unknown>,
		payloadTransform?: (
			formValues: Record<string, unknown>,
			projectId: number
		) => Record<string, unknown>
	) => Promise<{ id: number; name: string }>;
	unresolvedCount: number;
	lookupData: LookupData;
}

const CONFIDENCE_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
	exact: 'success',
	high: 'success',
	medium: 'warning',
	low: 'error',
	none: 'default',
};

export const StepResolveRelations: React.FC<StepResolveRelationsProps> = ({
	groups,
	onResolve,
	onSkip,
	onAutoResolve,
	onCreateRecord,
	unresolvedCount,
	lookupData,
}) => {
	const { t } = useTranslation();
	const totalGroups = groups.length;
	const resolvedGroups = groups.filter((g) => g.resolution.status !== 'pending').length;

	if (groups.length === 0) {
		return <Alert severity="success">{t('bulkUpload.resolve.noRelations')}</Alert>;
	}

	const highConfidenceCount = groups.filter(
		(g) =>
			g.resolution.status === 'pending' &&
			(g.scoreResult.confidence === 'exact' || g.scoreResult.confidence === 'high')
	).length;

	return (
		<Box>
			{/* Progress bar */}
			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
					<Typography variant="body2">
						{t('bulkUpload.resolve.resolved', { resolved: resolvedGroups, total: totalGroups })}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t('bulkUpload.resolve.remaining', { count: unresolvedCount })}
					</Typography>
				</Box>
				<LinearProgress variant="determinate" value={(resolvedGroups / totalGroups) * 100} />
			</Box>

			{/* Auto-resolve button */}
			{highConfidenceCount > 0 && (
				<Button
					variant="outlined"
					startIcon={<AutoFixHighIcon />}
					onClick={onAutoResolve}
					sx={{ mb: 2 }}>
					{t('bulkUpload.resolve.autoResolve', { count: highConfidenceCount })}
				</Button>
			)}

			{/* Resolution cards */}
			{groups.map((group) => (
				<ResolutionCard
					key={group.key}
					group={group}
					onResolve={onResolve}
					onSkip={onSkip}
					onCreateRecord={onCreateRecord}
					lookupData={lookupData}
				/>
			))}
		</Box>
	);
};

// ---------------------------------------------------------------------------
// Individual Resolution Card
// ---------------------------------------------------------------------------

const ResolutionCard: React.FC<{
	group: ResolutionGroup;
	onResolve: (key: string, selectedId: number | null, selectedLabel: string | null) => void;
	onSkip: (key: string) => void;
	onCreateRecord: (
		groupKey: string,
		contextKey: string,
		apiEndpoint: string,
		payload: Record<string, unknown>,
		payloadTransform?: (
			formValues: Record<string, unknown>,
			projectId: number
		) => Record<string, unknown>
	) => Promise<{ id: number; name: string }>;
	lookupData: LookupData;
}> = ({ group, onResolve, onSkip, onCreateRecord, lookupData }) => {
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState('');
	const [showCreateForm, setShowCreateForm] = useState(false);
	const isResolved = group.resolution.status !== 'pending';

	// Build options from candidates + extra search results
	const contextKey = group.field.relation?.contextKey ?? '';
	const candidates = group.scoreResult.candidates;
	const allLookupItems = lookupData[contextKey] ?? [];

	const matchField = group.field.relation?.matchField ?? 'name';
	const alternateMatchFields = group.field.relation?.alternateMatchFields;

	const searchResults =
		searchTerm.length >= 2
			? searchLookup(searchTerm, lookupData[contextKey] ?? [], matchField, alternateMatchFields)
			: [];

	// Always include full lookup options so newly-created records appear immediately
	// in subsequent unresolved rows, even when there are existing fuzzy candidates.
	const lookupOptions: ScoredCandidate[] = allLookupItems.map((item) => ({
		id: item.id,
		label: String(item[matchField] ?? item.name ?? String(item.id)),
		score: 0,
		matchType: 'fuzzy' as const,
	}));

	// Merge candidates, search results, and lookup options — dedup by id
	const seenIds = new Set<number>();
	const allOptions: ScoredCandidate[] = [];
	for (const opt of [...candidates, ...searchResults, ...lookupOptions]) {
		if (!seenIds.has(opt.id)) {
			seenIds.add(opt.id);
			allOptions.push(opt);
		}
	}

	// Check if inline creation is available for this relation
	const relationEntityType = group.field.relation?.entityType;
	const createCheck = relationEntityType ? canCreateInline(relationEntityType, lookupData) : null;

	return (
		<Card
			variant="outlined"
			sx={{
				mb: 1.5,
				opacity: isResolved ? 0.7 : 1,
				borderColor: isResolved ? 'success.main' : undefined,
			}}>
			<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
					<Box sx={{ flex: 1 }}>
						{/* Field label and raw value */}
						<Typography variant="subtitle2">
							{group.field.label}:{' '}
							<Typography component="span" sx={{ fontStyle: 'italic' }}>
								&ldquo;{group.rawValue}&rdquo;
							</Typography>
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{t('bulkUpload.resolve.affectsRows', { count: group.rowCount })}
						</Typography>
					</Box>

					{/* Confidence badge */}
					<Chip
						label={group.scoreResult.confidence}
						color={CONFIDENCE_COLORS[group.scoreResult.confidence]}
						size="small"
						sx={{ ml: 1 }}
					/>
				</Box>

				{isResolved ? (
					<Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
						{group.resolution.status === 'accepted' || group.resolution.status === 'created' ? (
							<>
								<CheckCircleIcon color="success" fontSize="small" />
								<Typography variant="body2" component="span">
									→ {group.resolution.selectedLabel}
									{group.resolution.status === 'created' && (
										<Chip label="new" size="small" color="info" sx={{ ml: 1 }} />
									)}
								</Typography>
							</>
						) : group.resolution.status === 'skipped' ? (
							<>
								<SkipNextIcon color="disabled" fontSize="small" />
								<Typography variant="body2" color="text.secondary">
									{t('bulkUpload.resolve.skippedRowsWillHaveErrors')}
								</Typography>
							</>
						) : null}
						<Button
							size="small"
							onClick={() => onResolve(group.key, null, null)}
							sx={{ ml: 'auto' }}>
							{t('bulkUpload.resolve.undo')}
						</Button>
					</Box>
				) : (
					<Box sx={{ mt: 1 }}>
						{/* Autocomplete for selecting from lookup */}
						<Autocomplete
							size="small"
							options={allOptions}
							getOptionLabel={(opt) =>
								typeof opt === 'string'
									? opt
									: opt.score > 0
										? `${opt.label} (${Math.round(opt.score * 100)}%)`
										: opt.label
							}
							isOptionEqualToValue={(opt, val) => opt.id === val.id}
							onInputChange={(_, val) => setSearchTerm(val)}
							onChange={(_, val) => {
								if (val && typeof val !== 'string') {
									onResolve(group.key, val.id, val.label);
								}
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label={t('bulkUpload.resolve.selectItem', {
										entityType: group.field.relation?.entityType ?? 'item',
									})}
									placeholder={`${t('search')}...`}
								/>
							)}
							renderOption={(props, opt) => (
								<li {...props} key={opt.id}>
									<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
										<span>{opt.label}</span>
										{opt.score > 0 && (
											<Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
												{Math.round(opt.score * 100)}%
											</Typography>
										)}
									</Box>
								</li>
							)}
						/>

						{/* Action buttons: Skip + Create New */}
						<Box
							sx={{
								mt: 1,
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
							}}>
							<Button
								size="small"
								color="inherit"
								startIcon={<SkipNextIcon />}
								onClick={() => onSkip(group.key)}>
								{t('bulkUpload.resolve.skip')}
							</Button>

							{createCheck?.canCreate && (
								<Button
									size="small"
									color="primary"
									startIcon={<AddCircleOutlineIcon />}
									onClick={() => setShowCreateForm(!showCreateForm)}>
									{showCreateForm ? t('cancel') : `Create New ${group.field.label}`}
								</Button>
							)}
							{createCheck && !createCheck.canCreate && !createCheck.noSchema && (
								<Tooltip
									title={`Cannot create: requires ${createCheck.missingRelations.join(', ')}`}>
									<span>
										<Button
											size="small"
											color="primary"
											startIcon={<AddCircleOutlineIcon />}
											disabled>
											Create New {group.field.label}
										</Button>
									</span>
								</Tooltip>
							)}
						</Box>

						{/* Inline creation form */}
						{createCheck?.canCreate && (
							<Collapse in={showCreateForm}>
								<InlineCreateForm
									group={group}
									targetSchema={createCheck.schema}
									lookupData={lookupData}
									onCreateRecord={onCreateRecord}
									onCancel={() => setShowCreateForm(false)}
								/>
							</Collapse>
						)}
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

// ---------------------------------------------------------------------------
// Inline Create Form — renders required fields from target schema
// ---------------------------------------------------------------------------

const InlineCreateForm: React.FC<{
	group: ResolutionGroup;
	targetSchema: UploadSchema;
	lookupData: LookupData;
	onCreateRecord: (
		groupKey: string,
		contextKey: string,
		apiEndpoint: string,
		payload: Record<string, unknown>,
		payloadTransform?: (
			formValues: Record<string, unknown>,
			projectId: number
		) => Record<string, unknown>
	) => Promise<{ id: number; name: string }>;
	onCancel: () => void;
}> = ({ group, targetSchema, lookupData, onCreateRecord, onCancel }) => {
	const { t } = useTranslation();

	// Only show required fields + name (if optional but present)
	const editableFields = targetSchema.fields.filter((f) => f.required || f.name === 'name');

	// Pre-populate from the raw unresolved value:
	//   - 'name' field gets the raw value (standard pattern)
	//   - Other fields respect their defaultValue first, only populate with raw value if no default
	const initialValues: Record<string, unknown> = {};
	let firstRequiredStringPopulated = false;
	for (const field of editableFields) {
		if (field.name === 'name') {
			initialValues[field.name] = group.rawValue;
		} else if (field.defaultValue !== undefined) {
			// Respect explicit defaults (e.g., Currency: 'USD')
			initialValues[field.name] = field.defaultValue;
		} else if (
			!firstRequiredStringPopulated &&
			field.required &&
			field.dataType === 'string' &&
			!field.relation
		) {
			// Only use raw value for first required string field if it has no default
			initialValues[field.name] = group.rawValue;
			firstRequiredStringPopulated = true;
		} else {
			initialValues[field.name] = '';
		}
	}

	const [formValues, setFormValues] = useState<Record<string, unknown>>(initialValues);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	const handleChange = (fieldName: string, value: unknown) => {
		setFormValues((prev) => ({ ...prev, [fieldName]: value }));
		setFormError(null);
	};

	const handleSubmit = async () => {
		// Basic required field validation
		for (const field of editableFields) {
			if (field.required && !formValues[field.name] && formValues[field.name] !== 0) {
				setFormError(`${field.label} is required`);
				return;
			}
		}

		setIsSubmitting(true);
		setFormError(null);
		try {
			const contextKey = group.field.relation?.contextKey ?? '';
			await onCreateRecord(
				group.key,
				contextKey,
				targetSchema.apiEndpoint,
				formValues,
				targetSchema.payloadTransform
			);
			setIsSubmitting(false);
			onCancel(); // Close form on success
		} catch (err) {
			setFormError(err instanceof Error ? err.message : 'Failed to create record');
			setIsSubmitting(false);
		}
	};

	return (
		<Box sx={{ mt: 1.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
			<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
				Create New {targetSchema.label.replace(/s$/, '')}
			</Typography>

			<Stack spacing={1.5}>
				{editableFields.map((field) => (
					<InlineField
						key={field.name}
						field={field}
						value={formValues[field.name]}
						onChange={(val) => handleChange(field.name, val)}
						lookupData={lookupData}
					/>
				))}
			</Stack>

			{formError && (
				<Alert severity="error" sx={{ mt: 1.5 }}>
					{formError}
				</Alert>
			)}

			<Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
				<Button size="small" onClick={onCancel} disabled={isSubmitting}>
					{t('cancel')}
				</Button>
				<Button
					size="small"
					variant="contained"
					onClick={handleSubmit}
					disabled={isSubmitting}
					startIcon={isSubmitting ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}>
					{isSubmitting ? 'Creating...' : 'Create'}
				</Button>
			</Box>
		</Box>
	);
};

// ---------------------------------------------------------------------------
// Individual field renderer for the inline form
// ---------------------------------------------------------------------------

const InlineField: React.FC<{
	field: UploadFieldDef;
	value: unknown;
	onChange: (value: unknown) => void;
	lookupData: LookupData;
}> = ({ field, value, onChange, lookupData }) => {
	const label = `${field.label}${field.required ? ' *' : ''}`;

	switch (field.dataType) {
		case 'enum':
			return (
				<TextField
					select
					size="small"
					label={label}
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value)}
					fullWidth>
					{(field.enumValues ?? []).map((ev) => (
						<MenuItem key={ev} value={ev}>
							{ev}
						</MenuItem>
					))}
				</TextField>
			);

		case 'relation': {
			const items = lookupData[field.relation?.contextKey ?? ''] ?? [];
			return (
				<TextField
					select
					size="small"
					label={label}
					value={value ?? ''}
					onChange={(e) => onChange(Number(e.target.value))}
					fullWidth>
					{items.map((item) => (
						<MenuItem key={item.id} value={item.id}>
							{item.name}
						</MenuItem>
					))}
				</TextField>
			);
		}

		case 'number':
			return (
				<TextField
					size="small"
					type="number"
					label={label}
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
					fullWidth
					inputProps={{
						min: field.constraints?.min,
						max: field.constraints?.max,
					}}
				/>
			);

		case 'date':
			return (
				<TextField
					size="small"
					type="date"
					label={label}
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value)}
					fullWidth
					InputLabelProps={{ shrink: true }}
				/>
			);

		case 'boolean':
			return (
				<TextField
					select
					size="small"
					label={label}
					value={value === true ? 'true' : value === false ? 'false' : ''}
					onChange={(e) => onChange(e.target.value === 'true')}
					fullWidth>
					<MenuItem value="true">Yes</MenuItem>
					<MenuItem value="false">No</MenuItem>
				</TextField>
			);

		case 'string':
		default:
			return (
				<TextField
					size="small"
					label={label}
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value)}
					fullWidth
					inputProps={{
						maxLength: field.constraints?.maxLength,
					}}
				/>
			);
	}
};
